/**
 * geraet.ts — Browser- und System-Familie aus einem User-Agent-Text.
 *
 * Gemeinsam genutzt von:
 *   - `lib/push.ts` → Gerätebezeichnung beim Abonnieren von Push
 *     („Chrome auf Windows"), gespeichert in `push_subscriptions.device_label`
 *   - Admin-Ansicht „Geräte" (Migration 131) → Supabase speichert den vollen
 *     User-Agent je Sitzung; angezeigt wird nur Browser + System.
 *
 * Bewusst grob (zwei Achsen, keine Versionen): Für „Wie viele Geräte nutzen
 * dieses Konto?" reicht das, und es bleibt datensparsam.
 *
 * @module lib/geraet
 */

/** Browser- und System-Familie; `null`, wenn nicht erkannt. */
export interface GeraetInfo {
  browser: string | null;
  system: string | null;
}

/**
 * Erkennt die Browser-Familie.
 * Reihenfolge wichtig: Edge enthält „Chrome", Chrome enthält „Safari".
 *
 * @param ua - User-Agent-Text
 * @returns z. B. „Edge", „Chrome", „Safari" oder `null`
 */
export function erkenneBrowser(ua: string): string | null {
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/OPR\/|Opera/i.test(ua)) return 'Opera';
  if (/Firefox/i.test(ua)) return 'Firefox';
  if (/Chrome/i.test(ua)) return 'Chrome';
  if (/Safari/i.test(ua)) return 'Safari';
  return null;
}

/**
 * Erkennt die System-Familie. iPad und iPod zählen wie bisher als „iPhone"
 * (unverändert aus `lib/push.ts` übernommen).
 *
 * @param ua - User-Agent-Text
 * @returns z. B. „iPhone", „Android", „Windows" oder `null`
 */
export function erkenneSystem(ua: string): string | null {
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) return 'Android';
  if (/Windows/i.test(ua)) return 'Windows';
  if (/Macintosh|Mac OS/i.test(ua)) return 'Mac';
  if (/Linux/i.test(ua)) return 'Linux';
  return null;
}

/**
 * Zerlegt einen User-Agent in Browser- und System-Familie.
 *
 * @param ua - User-Agent-Text (darf leer oder `null` sein)
 */
export function geraetAusUserAgent(ua: string | null | undefined): GeraetInfo {
  const text = ua || '';
  return { browser: erkenneBrowser(text), system: erkenneSystem(text) };
}

/**
 * Kurze, sprachneutrale Anzeige wie „Edge · Windows". Markennamen werden
 * nicht übersetzt; nur der Fall „nichts erkannt" braucht einen Text.
 *
 * @param ua - User-Agent-Text
 * @param unbekannt - übersetzter Text für „Unbekanntes Gerät"
 */
export function geraetKurz(ua: string | null | undefined, unbekannt: string): string {
  const { browser, system } = geraetAusUserAgent(ua);
  if (browser && system) return browser + ' · ' + system;
  return browser || system || unbekannt;
}
